import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { Commands, Downloads, Results, Search } from '@local/client-contracts';
import { PopupService, UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { getFileType } from '@shared/consts/file-types';
import { EventInfo, EventsService, LogService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { KeyboardService } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { RouterService } from '@shared/services/router.service';
import { isChangeFavoriteStatusCommand } from '@shared/utils';
import { getDownloadIcon } from '@shared/utils/set-icon.util';
import { WorkspacesService } from 'src/app/bar/services';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { CommandBarService } from 'src/app/bar/services/command-bar.service';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { ContextMenuInvokeSource } from 'src/app/bar/services/commands/results-item-context-menu-helper';
import { FavoritesService } from 'src/app/bar/services/favorites.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { UrlResolverService } from 'src/app/bar/services/url-resolver.service';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { ResultCommandBuilder, ResultContextMenuItem, ResultContextMenuItemId, SearchResults, TelemetryTrigger } from '../..';
import { FilePreviewType } from '../../../preview/file-preview/file-preview-types';
import { ViewItemComponent, onChangeMode } from '../../../results-views/view-item/view-item.component';
import { InvokeCommand } from '../../models/invoke-command.model';
import { StaticResultsCommandBuilder } from './static-results-command-builder.service';
import { StaticResultsContextMenuHelper } from './static-results-context-menu-helper';
import { SearchPopUpService } from '../../../special-views/search-popup/search-popup.service';
import { Constants } from '@local/common';

@UntilDestroy()
@Component({
  selector: 'static-result-item',
  templateUrl: './static-result-item.component.html',
  styleUrls: ['./static-result-item.component.scss'],
})
export class StaticResultItemComponent extends ViewItemComponent<Search.CollectionResultItem> implements OnInit {
  protected showResultSectionsDefault: Results.ShowResultSections = {
    showBullets: false,
    showContextMenu: false,
    showSubtitleInSecondLine: false,
    showSubtitle: true,
    hasResource: false,
    showPreviewIcon: true,
    showSummaryIcon: true,
    showPreviewIconContextMenu: true,
  };

  readonly URL_ITEM_ICON_FALLBACK: UiIconModel = {
    type: 'font',
    value: 'icon-link2',
  };

  @HostBinding('class.header-title') get isHeader() {
    return this.model.type === 'collection-header';
  }

  @Input() lightTitle: boolean;

  _inputPlaceholder: string;
  @Input() set inputPlaceholder(val: string) {
    this._inputPlaceholder = val;
  }
  get inputPlaceholder() {
    return this._inputPlaceholder;
  }

  defaultInputModel: string;
  @Output() onRenameFlow = new EventEmitter<boolean>();

  constructor(
    hostRef: ElementRef,
    protected cdr: ChangeDetectorRef,
    logService: LogService,
    protected applicationsService: ApplicationsService,
    contextMenuService: ContextMenuService,
    protected resultCommandBuilder: ResultCommandBuilder,
    hubService: HubService,
    eventsService: EventsService,
    downloadsService: DownloadsService,
    resourcesService: ResourcesService,
    commandsService: CommandsService,
    commandBarService: CommandBarService,
    protected popupService: PopupService,
    protected keyboardService: KeyboardService,
    routerService: RouterService,
    errorHandler: GlobalErrorHandler,
    resultsService: ResultsService,
    protected flagsService: FlagsService,
    private collectionResultsCommandBuilder: StaticResultsCommandBuilder,
    private blobService: BlobsService,
    protected favoritesService: FavoritesService,
    private urlResolverService: UrlResolverService,
    protected workspacesService: WorkspacesService,
    protected searchPopUpService: SearchPopUpService
  ) {
    super(
      hostRef,
      cdr,
      logService,
      applicationsService,
      contextMenuService,
      resultCommandBuilder,
      hubService,
      eventsService,
      downloadsService,
      resourcesService,
      commandsService,
      commandBarService,
      popupService,
      keyboardService,
      routerService,
      errorHandler,
      resultsService,
      flagsService,
      favoritesService,
      workspacesService,
      searchPopUpService
    );
  }

  protected async updateThumbnailUrl(): Promise<string> {
    if (this.isHeader) {
      this.thumbnail = undefined;
      return;
    }
    if (this.model.type === 'collection-file') {
      if (!this.model.view?.thumbnail?.url) {
        this.thumbnail = undefined;
        return;
      }
      const mimeType = this.model?.['meta']?.mimeType;
      const filePreviewType: FilePreviewType = getFileType(mimeType);
      const blackList = ['audio', 'text', 'md', 'code'];
      if (!filePreviewType || blackList.includes(filePreviewType)) {
        this.thumbnail = undefined;
        return;
      }
      const url = await this.blobService.getCdnUrl(this.model.view.thumbnail.url);
      this.thumbnail = url;
    }

    if (this.model.type === 'collection-url') {
      const urlModel = this.model as Search.CollectionUrlResultItem;
      let thumbnail: string = urlModel.thumbnail;
      if (!thumbnail || urlModel.thumbnailExpired < Date.now()) {
        const urlData = await this.urlResolverService.resolveSite(urlModel.url);
        thumbnail = urlData?.banner;
      }
      this.thumbnail = thumbnail;
    }

    return this.thumbnail;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.defaultInputModel = this.model.type === 'collection-header' ? 'Header' : this.model.view.title.text;
    this.buildDragData();
    this.cdr.markForCheck();
  }

  onChanges(val: onChangeMode) {
    super.onChanges(val);

    if (this.model.showResultSections) {
      if (this.showResultSections) {
        this.resetShowResultSections();
      }
      this.checkShowResultSections();
    }
    if (this.model?.['meta']) {
      this.updateThumbnailUrl();
    }
  }

  checkShowResultSections() {
    this.setShowResultSections(this.model.showResultSections);
    this.setShowResultSections(this.showResultSectionsDefault, false);
  }

  buildDragData() {
    if (!this.model || this.model.type !== 'collection-file') return;
    const icon = getDownloadIcon(this.model.view?.icon);
    this.dragMetadata = {
      type: 'Url',
      icon,
      eventInfo: {
        search: {
          clientSearchId: this.clientSearchId,
          sessionId: this.sessionId,
        },
        position: this.index,
        list: this.listName,
      },
    };
    this.dragData = {
      metadata: this.dragMetadata,
      model: this.model.view?.title,
    };
    this.cdr.markForCheck();
  }

  protected initContextMenuHelper(): void {
    this.contextMenuHelper = new StaticResultsContextMenuHelper(
      this.collectionResultsCommandBuilder,
      this.logger,
      this.contextMenuService,
      this.hostRef,
      this.titleRef,
      this.subtitleRef,
      this.actionsRef,
      this.errorHandler,
      this.resourcesService,
      this.hubService,
      this.flagsService,
      this.workspacesService,
      this.blobService
    );
  }

  initContextMenuHelperListeners() {
    this.contextMenuHelper.invoke$.pipe(untilDestroyed(this)).subscribe(({ item, trigger }) => this.onContextMenuCommand(item, trigger));
  }

  async openContextMenu(event: MouseEvent, source: ContextMenuInvokeSource) {
    const searchModel: SearchResults = this.model as SearchResults;

    const ev: Partial<EventInfo> & { label: ResultContextMenuItemId | 'open_menu' } = {
      label: 'open_menu',
      location: { title: this.getLocation() },
      resources: this.getEventResources(),
    };
    this.sendEvent(ev);
    const isPreviewAvailable =
      this.isPreviewClickAction(searchModel?.action) &&
      this.showResultSections.showPreviewIconContextMenu &&
      !!this.model?.['meta'].mimeType;
    const ref = await this.contextMenuHelper.open(event, this.model, source, 'popup', null, false, true, true, 5, isPreviewAvailable);
    ref.close$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextClose.emit();
      this.cdr.markForCheck();
    });
    this.contextOpen.emit();
  }

  async onContextMenuCommand(item: ResultContextMenuItem, trigger: TelemetryTrigger, source?: string) {
    let target: string = item.id;
    let invoked = false;
    if (this.model.type === 'collection-header') {
      target = item.id === 'rename' ? 'header_rename' : `${item.id}_header`;
    }
    this.eventsService.event('collections.collection_actions', {
      target,
      label: trigger === 'context_menu_click' ? 'mouse_click' : 'keyboard',
      location: { title: this.hubService.currentLocation },
    });

    if (isChangeFavoriteStatusCommand(item.command)) {
      this.toggleFavorite(trigger);
      invoked = true;
    }

    if (item.id === 'rename') {
      this.showResultSections.showInputTitle = true;
      this.cdr.markForCheck();
    }

    if (item.id === 'download') {
      const command = item.command as Commands.DownloadUrl;
      const req: Downloads.UrlDownloadRequest = {
        type: 'Url',
        url: command.url,
        grantType: command.grantType,
      };
      await this.downloadsService.urlDownload(req);
      invoked = true;
    }

    const eventInfo: Partial<EventInfo> = {
      target: trigger,
      label: item.command.type,
      location: {
        title: this.hubService.currentLocation,
      },
    };
    if (invoked) {
      return;
    }
    this.invoke.emit({ ...item.command, invokerItem: { ...this.model }, command: item.command, eventInfo });
  }

  async onIconError() {
    if (this.model.view) this.model.view.icon = this.fallbackIcon;
  }

  onDisplayInput(event) {
    this.resultInputTitle = event;
    super.displayInput();
  }

  onBlurUInput(event) {
    const { inputElement, defaultInputModel } = event;
    this.resultInputTitle = inputElement;
    super.onBlurUInput(inputElement, defaultInputModel);
    this.cdr.markForCheck();
  }

  protected async toggleFavoriteAction(trigger: TelemetryTrigger, via?: string) {
    this.toggleFavorite(trigger);
    this.cdr.markForCheck();
    const jsonData = via ? { 'action.trigger': via } : null;
    this.invoke.emit({
      command: { type: 'change-favorite-status', status: this.model.isFavorite ? 'add' : 'remove' } as Commands.ChangeFavoriteStatus,
      trigger,
      invokerItem: this.model,
      eventInfo: {
        label: `${this.model.isFavorite ? 'add' : 'remove'}_favorite`,
        target: trigger,
        jsonData: jsonData ?? undefined,
      },
    });
    const label = `${this.model.isFavorite ? 'add' : 'remove'}_favorite`; // inverse since we already toggled
    this.sendEvent({
      label,
      location: { title: this.getLocation() },
      resources: this.getEventResources(),
    });
  }

  protected toggleFavorite(trigger: TelemetryTrigger): void {
    if (!this.workspacesService.isFeatureDisabled(Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG)) {
      super.toggleFavorite(trigger);
      this.model.isFavorite = !this._model.isFavorite;
      const status = this.model.isFavorite;
      this.cdr.markForCheck();
      if (status) {
        this.favoritesService.create({ id: this.model.id, type: 'collection-item' });
      } else {
        this.favoritesService.delete(this.model.id);
      }
    }
  }

  galleryInvoke($event) {
    switch ($event.type) {
      case 'contextmenu':
        this.openContextMenu($event.event, 'actions');
        break;
      case 'summary':
        this.onSummary($event.event);
        break;
      case 'preview':
        this.onPreviewClick();
        if (this.isClickable) {
          $event.event.stopPropagation();
        }
        break;
      case 'title':
        this.onInvoke({ command: this.model?.view?.title?.onClick, source: 'title' });
        break;
      case 'checkbox':
        this.changeSelection.emit($event.event);
        break;
      case 'onDisplayInput':
        this.onDisplayInput($event.event);
        break;
      case 'onBlurUInput':
        this.onBlurUInput($event.event);
        break;
      case 'onClickOut':
        this.onEditTitleClickOut();
        break;
    }
  }

  async onInvoke(invokeData: InvokeCommand): Promise<void> {
    this.eventsService.event('collections.redirect', {
      target: invokeData.trigger,
      location: { title: this.hubService.currentLocation },
      resources: [{ ...this.getEventResource('collection'), appId: 'collections', type: 'collections' }],
    });
    super.onInvoke(invokeData);
  }

  onTitleClick() {
    if (this.model.type !== 'collection-header') return;
    this.model.showResultSections.showInputTitle = true;
    this.showResultSections.showInputTitle = true;
    this.cdr.markForCheck();
    this.onRenameFlow.emit(true);
  }
}
